import { type I18nKey, localeOptions } from '@/i18n/locales';
import type { LocalLanguageType, LocalOption } from '@/i18n/types';
import { enUS, zhCN } from '@ts-max/antd';
import ant_en_US from 'antd/lib/locale/en_US';
import ant_zh_CN from 'antd/lib/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/zh-cn';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * 获取项目中第三方库使用的语言配置
 */
export function getLocales(language: string) {
  const lang = normalizeLanguage(language);

  // 英文
  if (lang.startsWith('en')) {
    return {
      antLocale: ant_en_US,
      tsMaxLocale: enUS,
    };
  }

  // 默认中文
  return {
    antLocale: ant_zh_CN,
    tsMaxLocale: zhCN,
  };
}

export const defaultLanguage: LocalLanguageType = 'zh-CN';

/**
 * 将语言代码标准化：en* -> en-US, zh* -> zh-CN
 */
export function normalizeLanguage(lng: string): LocalLanguageType {
  if (!lng) return defaultLanguage;

  if (lng.startsWith('en')) {
    return 'en-US';
  }

  if (lng.startsWith('zh')) {
    return 'zh-CN';
  }

  // 不支持的语言，返回默认语言
  return defaultLanguage;
}

/**
 * 自定义语言检测器，在检测到语言后进行标准化
 * 优先级：URL参数 > localStorage > 浏览器语言
 */
export const customLanguageDetector = new LanguageDetector().addDetector({
  name: 'normalizeLanguageDetector',
  lookup: () => {
    // 1. 从 URL 参数中检测（优先级最高）
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang) {
      return normalizeLanguage(urlLang);
    }

    // 2. 从 localStorage 中检测
    const fromStorage = localStorage.getItem('i18nextLng');
    if (fromStorage) {
      return normalizeLanguage(fromStorage);
    }

    // 3. 从浏览器语言中检测
    const browserLang = navigator.language || (navigator as any).userLanguage;
    if (browserLang) {
      return normalizeLanguage(browserLang);
    }

    return defaultLanguage;
  },
  cacheUserLanguage: (lng: string) => {
    localStorage.setItem('i18nextLng', lng);
  },
});

/**
 * 获取项目中支持的语言选项
 */
export function getLocaleOptions(): LocalOption[] {
  return localeOptions;
}

/**
 * 当前语言
 */
export function getLanguage(): LocalLanguageType {
  return normalizeLanguage(i18n.language);
}

/**
 * 切换语言
 */
export async function changeLanguage(lng: LocalLanguageType) {
  const language = normalizeLanguage(lng);

  await i18n.changeLanguage(language);

  if (language?.startsWith('en')) {
    dayjs.locale('en');
  } else {
    dayjs.locale('zh-cn');
  }

  return language;
}

/**
 * 是否是中文环境
 * */
export const isZhCN = () => getLanguage() === 'zh-CN';

/**
 * 获取国际化 key
 *
 * 作用：编写key时能有TS提示以及TS类型检查
 * */
export const i18nKey = {
  t: (key: I18nKey) => key,
};
